import type { QueryObserverResult } from '@tanstack/query-core';
import { useQueries, type QueryFunction, type QueryFunctionContext, type UseQueryOptions } from '@tanstack/vue-query';
import { computed, type Ref, unref } from 'vue';
import pLimit from 'p-limit';

export type LimitedQueriesResult<T, E = Error> = Readonly<Ref<QueryObserverResult<T, E>[]>>;

/** Shared across Discover lists, portfolio-tree calcs, and other `limitedQueryFn` callers. */
export const QUERY_FN_CONCURRENCY_LIMIT = 10;

const limit = pLimit(QUERY_FN_CONCURRENCY_LIMIT);

export function limitedQueryFn<T>(fn: QueryFunction<T>): QueryFunction<T> {
  return (context) => limit(() => fn(context));
}

type QueryWithFn<T, E> = UseQueryOptions<T, E> & { queryFn: unknown };

function wrapQueryFnIfPresent<T>(query: T): T {
  if (query == null || typeof query !== 'object' || !('queryFn' in query)) {
    return query;
  }

  const options = query as T & { queryFn?: unknown };
  const rawQueryFn = unref(options.queryFn);
  if (typeof rawQueryFn !== 'function') {
    return query;
  }

  options.queryFn = limitedQueryFn(rawQueryFn as QueryFunction);
  return query;
}

/**
 * Wrap static `useQueries` queryFns with the same global p-limit as {@link useLimitedQueries}.
 * DataLayer mounts many Discover composables at once; without this they all fire in parallel.
 */
export function withLimitedQueryFns<T extends readonly unknown[]>(queries: [...T]): [...T] {
  for (const query of queries) {
    wrapQueryFnIfPresent(query);
  }
  return queries;
}

/**
 * A custom wrapper for `useQueries` that applies `p-limit` to all `queryFn` functions.
 * This ensures that query functions are executed with concurrency control, limiting the
 * number of simultaneous executions of their promises.
 *
 * When dealing with large portfolios containing hundreds of tracks, sending hundreds of API
 * requests simultaneously can overwhelm both the client (e.g., browser) and the backend (e.g.,
 * calculation bot or API server). This wrapper prevents such issues by controlling the number
 * of concurrent requests using `p-limit`. It ensures system stability by limiting API calls
 * and avoiding potential failures caused by resource overload.
 *
 * This is particularly useful for dynamic queries like in `useDataDiscoverCalc.ts` and `usePortfolioTreeCalculations.ts`.
 * Static Discover lists (DataLayer / `useDiscoverData`) should wrap `queryFn`s with {@link withLimitedQueryFns}
 * so a landing-page load cannot stampede `/gateway/web`.
 *
 * If there is a valid reason to use `useQueries` directly, explicitly disable the ESLint error
 */
export function useLimitedQueries<T, E = Error>(queries: Ref<UseQueryOptions<T, E>[]>) {
  const limitedQueries = computed(() =>
    queries.value.map((query) => {
      // Safely extract queryFn, whether it's a value or Ref
      const rawQueryFn = 'queryFn' in query ? unref((query as QueryWithFn<T, E>).queryFn) : undefined;

      if (typeof rawQueryFn !== 'function') {
        return query;
      }

      // Wrap the queryFn with p-limit
      const limitedQueryFnWithContext = (context: QueryFunctionContext) => limit(() => rawQueryFn(context));

      return {
        ...query,
        queryFn: limitedQueryFnWithContext,
      };
    }),
  );

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: limitedQueries,
  });
}
